import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  OnInit,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Params, Router, RouterLink } from '@angular/router';
import { Permissions } from '@app/core/guard/Permissions';
import { Roles } from '@app/core/guard/Roles';
import { MeetingFilterRequest } from '@interfaces/meetings/requests/meeting.request';
import { MeetingDto } from '@interfaces/meetings/responses/meeting.response';
import {
  MeetingClientVisibilityFilter,
  MeetingVisibilityCountsDto,
} from '@interfaces/meetings/responses/meeting-visibility.response';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from '@services/helpers/confirmation.service';
import { DateFormatterService } from '@services/helpers/date-formatter.service';
import { PermissionsService } from '@services/helpers/permissions.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { UtilsService } from '@services/helpers/utils.service';
import { MeetingsHttpService } from '@services/http/meetings/meetings-http.service';
import { VaultBaseComponent } from '@src/app/shared/components/base/vault-base.component';
import { ActiveFilterConfig } from '@src/app/shared/interfaces/active-filter.interface';
import { SharedModule } from '@src/app/shared/shared.module';
import { MenuItem } from 'primeng/api';
import { PaginatorState } from 'primeng/paginator';
import { Observable, Subscription, filter, finalize, first } from 'rxjs';
import {
  mensagemDoErro,
  rotuloDoDealVinculado,
} from '@features/dashboard/meetings/components/meeting-presenter';
import { meetingLifecycleTexts } from '@features/dashboard/meetings/components/meeting-lifecycle-actions';
import { ModalLinkMeetingComponent } from '@features/dashboard/meetings/components/modal-link-meeting/modal-link-meeting.component';
import { MeetingsFilterComponent } from './components/meetings-filter/meetings-filter.component';
import { ModalUnlinkMeetingDealComponent } from './components/modal-unlink-meeting-deal/modal-unlink-meeting-deal.component';
import { ModalSendMeetingBotComponent } from './components/modal-send-meeting-bot/modal-send-meeting-bot.component';
import {
  AlternarBot,
  MeetingLifecycleRequest,
  MeetingRowComponent,
} from './components/meeting-row/meeting-row.component';
import {
  IndicadorDeMeetings,
  MeetingsKpisComponent,
} from './components/meetings-kpis/meetings-kpis.component';
import { MeetingsClientVisibilityChipsComponent } from './components/meetings-client-visibility-chips/meetings-client-visibility-chips.component';
import { MeetingVisibilityRulesDrawerComponent } from '@features/dashboard/meetings/components/meeting-visibility-rules-drawer/meeting-visibility-rules-drawer.component';
import { GrupoDePeriodo, agruparPorPeriodo } from './meetings-periods';
import {
  ABAS_DA_TELA,
  AbaDaTela,
  ESCOPO_DA_ABA,
  abaDaUrl,
  abaParaUrl,
  comBotDaUrl,
  mostrarSemGravacaoDaUrl,
  filtrosDaUrl,
  filtrosParaUrl,
  isArchivedTab,
} from './meetings-url-state';

/** Rótulo de cada aba, na ordem de `ABAS_DA_TELA`. */
const ROTULOS_DAS_ABAS: Record<AbaDaTela, string> = {
  recentes: 'Recentes',
  proximas: 'Próximas',
  todas: 'Todas',
  arquivadas: 'Arquivadas',
};

/**
 * Comercial › Meetings — as reuniões do Teams gravadas pelo Recall.ai (card 3112).
 *
 * **Layout (card 3162, design `Meetings.dc.html`):** cards de indicadores no topo, abas em pílula,
 * lista em card agrupada por período (Hoje, Amanhã, Esta semana, mês) e o paginador no rodapé. Clicar
 * numa reunião abre a tela de detalhe (`/dashboard/meetings/:id`), com vídeo e transcrição. As modais
 * de detalhes, gravação e transcrição saíram da lista.
 *
 * **Abas:** Recentes (as minhas que já começaram, ao vivo no topo), Próximas (as minhas futuras, com
 * o toggle "Bot") e Todas. O painel de filtros vale nas três (card 3221) e soma ao recorte da aba.
 * Recorte, "minhas" e ordem são do servidor (`Scope`). **Quem vê (card 3191):** o servidor devolve as de que participei, as que têm
 * empresa ou relacionamento bancário vinculado e as sem vínculo disponibilizadas para acesso geral.
 *
 * Só do time interno. O `PermissionGuard` olha `MEETING_LIST`, mas **não olha papel** — e o
 * backend recusa quem tem parceiro. Por isso a tela checa `hasRole(TENANT)` e, para quem não é,
 * mostra um aviso sem chamar a API.
 *
 * **Estado na URL:** aba, filtros, "com bot" de Próximas e página (conversão em
 * `meetings-url-state.ts`). A busca por texto fica de fora: é digitada e some com o F5.
 */
@Component({
  selector: 'app-meetings',
  standalone: true,
  // `RouterLink` explícito: o `SharedModule` não reexporta o `RouterModule` (pegadinha do README).
  imports: [
    CommonModule,
    RouterLink,
    SharedModule,
    MeetingsFilterComponent,
    ModalLinkMeetingComponent,
    ModalUnlinkMeetingDealComponent,
    ModalSendMeetingBotComponent,
    MeetingRowComponent,
    MeetingsKpisComponent,
    MeetingsClientVisibilityChipsComponent,
    MeetingVisibilityRulesDrawerComponent,
  ],
  templateUrl: './meetings.component.html',
  styleUrl: './meetings.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MeetingsComponent extends VaultBaseComponent implements OnInit {
  @ViewChild('modalVincular') modalVincular: ModalLinkMeetingComponent | null = null;
  @ViewChild('modalDesvincular') modalDesvincular: ModalUnlinkMeetingDealComponent | null = null;
  @ViewChild('modalEnviarBot') modalEnviarBot: ModalSendMeetingBotComponent | null = null;

  readonly breadcrumbs: MenuItem[] = [
    { label: 'Comercial' },
    { label: 'Meetings', routerLink: '/dashboard/meetings' },
  ];
  readonly home: MenuItem = { icon: 'pi pi-home', routerLink: '/dashboard/dashboard-management' };

  // ─────────────────────────── indicadores do topo ───────────────────────────

  /** `null` = carregando ou falhou (o card mostra "—"). */
  readonly totalProximas = signal<number | null>(null);
  readonly proxima = signal<MeetingDto | null>(null);
  readonly totalBots = signal<number | null>(null);
  readonly totalSemDeal = signal<number | null>(null);

  // ─────────────────────────── abas e lista ───────────────────────────

  readonly abasDaTela = ABAS_DA_TELA;
  readonly rotulos = ROTULOS_DAS_ABAS;
  readonly aba = signal<AbaDaTela>('recentes');
  /** Filtro rápido de Próximas: só as com bot (card "Bots agendados"). */
  readonly soComBot = signal(false);
  /**
   * Filtro padrão (02/10/2026): das que já começaram, só as com gravação ou em andamento. Ligado aqui, a lista
   * mostra também as sem gravação (remarcada, cancelada, bot não entrou, falha).
   */
  readonly mostrarSemGravacao = signal(false);
  /** Quantas o filtro padrão escondeu na consulta atual. */
  readonly ocultasSemGravacao = signal(0);

  readonly ehInterno = signal(false);
  /** Já sabemos o papel — antes disso a tela não mostra nem o aviso nem a lista. */
  readonly papelResolvido = signal(false);
  readonly ligacoes = signal<MeetingDto[]>([]);
  readonly total = signal(0);
  readonly carregando = signal(false);
  readonly erro = signal<string | null>(null);
  readonly filtroAberto = signal(false);
  readonly filtrosAtivos = signal<ActiveFilterConfig[]>([]);
  readonly filtros = signal<MeetingFilterRequest>({});
  readonly busca = signal('');
  /** Reunião com uma ação em andamento (toggle, acesso, desvincular): a linha fica travada. */
  readonly ocupada = signal<string | null>(null);

  readonly grupos = computed<GrupoDePeriodo<MeetingDto>[]>(() =>
    agruparPorPeriodo(
      this.ligacoes(),
      m => this.datas.parseDate(m.scheduledStartAt),
      this.datas.getTodayDate()
    )
  );

  /** Painel "Como funciona a visibilidade" (card 3803). */
  readonly rulesOpen = signal(false);
  /** Contagem das pílulas "Para o cliente" (card 3803); `null` = carregando ou falhou. */
  readonly visibilityCounts = signal<MeetingVisibilityCountsDto | null>(null);
  /** Pílula ativa: um estado só escolhido (no painel dá para escolher vários; aí nenhuma fica ativa). */
  readonly selectedClientVisibility = computed<MeetingClientVisibilityFilter | null>(() => {
    const selected = this.filtros().clientVisibility ?? [];
    return selected.length === 1 ? selected[0] : null;
  });
  private countsLoad?: Subscription;

  readonly pageSize = 20;
  readonly opcoesDePagina = [10, 20, 50, 100];
  readonly primeiraLinha = signal(0);
  readonly linhasPorPagina = signal(20);
  private page = 1;
  private carga?: Subscription;

  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly permissions = inject(PermissionsService);

  /** Arquivar / Restaurar (card 3786). */
  readonly canArchive = toSignal(this.permissions.hasPermission(Permissions.Meeting.ARCHIVE), {
    initialValue: false,
  });
  /** Excluir (card 3786); a linha ainda exige `canDelete` da reunião. */
  readonly canDelete = toSignal(this.permissions.hasPermission(Permissions.Meeting.DELETE), {
    initialValue: false,
  });

  constructor(
    translateService: TranslateService,
    private readonly meetingsService: MeetingsHttpService,
    private readonly permissionsService: PermissionsService,
    private readonly confirmationService: ConfirmationService,
    private readonly toast: ToastHelperService,
    private readonly utilsService: UtilsService,
    private readonly datas: DateFormatterService
  ) {
    super(translateService);
  }

  ngOnInit(): void {
    this.lerEstadoDaUrl(this.route.snapshot.queryParams);

    this.permissionsService
      .hasRole(Roles.TENANT)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(interno => {
        const jaCarregado = this.ehInterno();
        this.ehInterno.set(interno);
        this.papelResolvido.set(true);
        // Só busca quem pode: para os demais o backend recusa, e o aviso já explica.
        if (interno && !jaCarregado) {
          this.carregar();
          this.carregarIndicadores();
        }
      });
  }

  carregar(): void {
    this.loadVisibilityCounts();
    this.carga?.unsubscribe();
    this.carregando.set(true);
    this.erro.set(null);
    // O overlay global (`app-loading`, via UtilsService), como a tela de Tarefas.
    this.utilsService.setIsLoading(true, 'Carregando as meetings...');

    this.carga = this.meetingsService
      .getMeetings(
        this.page,
        this.linhasPorPagina(),
        this.busca(),
        this.filtrosDaAba(),
        ESCOPO_DA_ABA[this.aba()]
      )
      .pipe(
        first(),
        finalize(() => this.utilsService.setIsLoading(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: pagina => {
          this.carregando.set(false);
          this.ligacoes.set(pagina?.data ?? []);
          this.total.set(pagina?.total ?? 0);
          this.ocultasSemGravacao.set(pagina?.hiddenWithoutRecording ?? 0);
        },
        error: erro => {
          this.carregando.set(false);
          this.ligacoes.set([]);
          this.total.set(0);
          this.erro.set(mensagemDoErro(erro, 'Não foi possível carregar as meetings.'));
        },
      });
  }

  /**
   * Filtros que viajam na aba atual: os do painel valem nas três abas (card 3221) e somam ao recorte
   * dela. Em Próximas, o "com bot" troca a situação por "Agendado" — é o que ele significa.
   */
  private filtrosDaAba(): MeetingFilterRequest {
    // Arquivadas (card 3786): todas as arquivadas, sem o filtro padrão de gravação — quem arquivou quer achá-la.
    if (isArchivedTab(this.aba())) return { ...this.filtros(), archived: true };
    // Em Próximas o filtro padrão não muda nada (só futuras) e custaria uma contagem a mais no backend.
    const padrao: MeetingFilterRequest =
      this.mostrarSemGravacao() || this.aba() === 'proximas'
        ? this.filtros()
        : { ...this.filtros(), onlyRecordedOrLive: true };
    if (this.aba() === 'proximas' && this.soComBot()) return { ...padrao, statuses: ['Scheduled'] };
    return padrao;
  }

  // ─────────────────────────── "Para o cliente" (card 3803) ───────────────────────────

  /**
   * Contagem das pílulas "Para o cliente": mesmos filtros e aba da lista, sem o próprio
   * `clientVisibility`. Falhou, as pílulas ficam sem número e o filtro continua funcionando.
   */
  private loadVisibilityCounts(): void {
    this.countsLoad?.unsubscribe();
    // `getVisibilityCounts` não manda `clientVisibility`: cada pílula conta sobre o resto do recorte.
    this.countsLoad = this.meetingsService
      .getVisibilityCounts(this.busca(), this.filtrosDaAba(), ESCOPO_DA_ABA[this.aba()])
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: counts => this.visibilityCounts.set(counts),
        error: () => this.visibilityCounts.set(null),
      });
  }

  /** Pílula escolhida: um estado, ou Todas (`null`). Vale o mesmo filtro do painel lateral. */
  protected chooseClientVisibility(value: MeetingClientVisibilityFilter | null): void {
    const next = { ...this.filtros() };
    if (value) next.clientVisibility = [value];
    else delete next.clientVisibility;
    this.handleFilter(next);
  }

  /** "Mostrar todas" / "Ocultar sem gravação": troca o filtro padrão e volta para a página 1. */
  protected alternarSemGravacao(): void {
    this.mostrarSemGravacao.update(v => !v);
    this.voltarParaPrimeiraPagina();
    this.carregar();
  }

  /**
   * Os três números do topo: o `total` de uma página de 1, sem busca. Falhou, o card fica "—" (a
   * lista mostra o erro, se for o caso).
   */
  private carregarIndicadores(): void {
    const total = (
      origem: Observable<{ total: number; data: MeetingDto[] } | null>,
      aplicar: (total: number | null, primeira: MeetingDto | null) => void
    ): void => {
      origem.pipe(first(), takeUntilDestroyed(this.destroyRef)).subscribe({
        next: pagina => aplicar(pagina?.total ?? 0, pagina?.data?.[0] ?? null),
        error: () => aplicar(null, null),
      });
    };

    total(this.meetingsService.getMeetings(1, 1, null, {}, 'Upcoming'), (n, primeira) => {
      this.totalProximas.set(n);
      this.proxima.set(primeira);
    });
    total(
      this.meetingsService.getMeetings(1, 1, null, { statuses: ['Scheduled'] }, 'Upcoming'),
      n => this.totalBots.set(n)
    );
    // "Sem deal" conta só as com gravação ou em andamento: a remarcada vazia não é deal a vincular.
    total(
      this.meetingsService.getMeetings(
        1,
        1,
        null,
        { onlyUnlinked: true, onlyRecordedOrLive: true },
        'All'
      ),
      n => this.totalSemDeal.set(n)
    );
  }

  // ─────────────────────────── abas, busca, filtros e página ───────────────────────────

  protected trocarAba(aba: AbaDaTela): void {
    if (aba === this.aba()) return;
    this.aba.set(aba);
    this.voltarParaPrimeiraPagina();
    this.carregar();
  }

  /**
   * Clique num card do topo. Os números são contados sem os filtros do painel; como o painel vale nas
   * três abas (card 3221), o clique limpa o filtro para a lista bater com o número do card.
   */
  protected escolherIndicador(indicador: IndicadorDeMeetings): void {
    if (indicador === 'proximas') {
      this.filtros.set({});
      this.soComBot.set(false);
      this.aba.set('proximas');
    } else if (indicador === 'bots') {
      this.filtros.set({});
      this.soComBot.set(true);
      this.aba.set('proximas');
    } else {
      this.filtros.set({ onlyUnlinked: true });
      this.aba.set('todas');
    }
    this.voltarParaPrimeiraPagina();
    this.carregar();
  }

  protected tirarSoComBot(): void {
    this.soComBot.set(false);
    this.voltarParaPrimeiraPagina();
    this.carregar();
  }

  handlePagination({ page = 0, rows = this.pageSize }: PaginatorState): void {
    this.page = page + 1;
    this.linhasPorPagina.set(rows);
    this.primeiraLinha.set(page * rows);
    this.gravarEstadoNaUrl();
    this.carregar();
  }

  handleSearch(busca: string = ''): void {
    const termo = busca.trim();
    if (termo === this.busca()) return;
    this.busca.set(termo);
    this.voltarParaPrimeiraPagina();
    this.carregar();
  }

  handleFilter(filtros: MeetingFilterRequest): void {
    this.filtros.set({ ...filtros });
    this.voltarParaPrimeiraPagina();
    this.filtroAberto.set(false);
    this.carregar();
  }

  onActiveFiltersChanged(configs: ActiveFilterConfig[]): void {
    this.filtrosAtivos.set(configs);
  }

  onFilterRemoved(propertyName: string): void {
    const novos = { ...this.filtros() };
    delete novos[propertyName as keyof MeetingFilterRequest];
    this.handleFilter(novos);
  }

  onAllFiltersCleared(): void {
    this.handleFilter({});
  }

  /** Página 1 no estado, no paginador e na URL. */
  private voltarParaPrimeiraPagina(): void {
    this.page = 1;
    this.primeiraLinha.set(0);
    this.gravarEstadoNaUrl();
  }

  /** Restaura aba, filtros e página da URL (link compartilhado ou F5). Valor inválido é ignorado. */
  private lerEstadoDaUrl(params: Params): void {
    this.filtros.set(filtrosDaUrl(params));
    this.aba.set(abaDaUrl(params));
    this.soComBot.set(comBotDaUrl(params));
    this.mostrarSemGravacao.set(mostrarSemGravacaoDaUrl(params));

    const tamanho = Number(params['pageSize']);
    this.linhasPorPagina.set(this.opcoesDePagina.includes(tamanho) ? tamanho : this.pageSize);
    const pagina = Number(params['page']);
    this.page = Number.isInteger(pagina) && pagina > 1 ? pagina : 1;
    this.primeiraLinha.set((this.page - 1) * this.linhasPorPagina());
  }

  /** Espelha aba, filtros e página na querystring, sem empilhar histórico. `null` remove a chave. */
  private gravarEstadoNaUrl(): void {
    const queryParams: Params = {
      aba: abaParaUrl(this.aba(), Object.keys(this.filtros()).length > 0),
      comBot: this.aba() === 'proximas' && this.soComBot() ? 'true' : null,
      semGravacao: this.mostrarSemGravacao() ? 'true' : null,
      page: this.page > 1 ? this.page : null,
      pageSize: this.linhasPorPagina() !== this.pageSize ? this.linhasPorPagina() : null,
    };
    Object.assign(queryParams, filtrosParaUrl(this.filtros()));
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  // ─────────────────────────── ações da linha ───────────────────────────

  /** Abre o detalhe; o "Voltar" de lá volta para esta URL (aba, filtros e página). */
  protected abrir(meeting: MeetingDto): void {
    this.router.navigate(['/dashboard/meetings', meeting.id], {
      state: { voltarPara: this.router.url },
    });
  }

  protected vincular(meeting: MeetingDto): void {
    this.modalVincular?.open(meeting);
  }

  /** Um vínculo: confirmação simples. Mais de um: a modal pergunta de qual. */
  protected desvincular(meeting: MeetingDto): void {
    if ((meeting.deals?.length ?? 0) > 1) {
      this.modalDesvincular?.open(meeting);
      return;
    }

    const link = meeting.deals[0];
    this.confirmationService
      .confirm(
        'Desvincular esta meeting do deal?',
        `"${meeting.title}" deixa de aparecer na aba Reuniões do deal ${rotuloDoDealVinculado(link)}, para todos que o veem. Ela pode ser vinculada de novo.`,
        'danger',
        undefined,
        'Desvincular'
      )
      .pipe(
        first(),
        filter(confirmado => confirmado)
      )
      .subscribe(() =>
        this.executar(meeting, this.meetingsService.unlinkDeal(meeting.id, link.dealId), {
          sucesso: 'Meeting desvinculada do deal.',
          falha: 'Não foi possível desvincular',
        })
      );
  }

  /** Disponibilizar para acesso geral ou voltar a privada, com confirmação. */
  protected alternarAcesso(meeting: MeetingDto): void {
    const disponibilizar = !meeting.isShared;
    this.confirmationService
      .confirm(
        disponibilizar ? 'Disponibilizar para acesso geral?' : 'Voltar a meeting a privada?',
        disponibilizar
          ? `Todo o time da Legatus passa a ver "${meeting.title}", com o vídeo e a transcrição. Qualquer participante pode desfazer.`
          : `Só quem participou de "${meeting.title}" volta a vê-la.`,
        disponibilizar ? 'default' : 'danger',
        undefined,
        disponibilizar ? 'Disponibilizar' : 'Voltar a privada'
      )
      .pipe(
        first(),
        filter(confirmado => confirmado)
      )
      .subscribe(() =>
        this.executar(meeting, this.meetingsService.setGeneralAccess(meeting.id, disponibilizar), {
          sucesso: disponibilizar
            ? 'Meeting disponível para o time da Legatus.'
            : 'Meeting privada de novo.',
          falha: 'Não foi possível mudar o acesso',
        })
      );
  }

  /** Toggle "Bot" da aba Próximas: desligar pede confirmação; ligar agenda na hora. */
  protected alternarBot({ meeting, ligar }: AlternarBot): void {
    if (ligar) {
      this.executar(meeting, this.meetingsService.rescheduleBot(meeting.id), {
        sucesso: 'Bot agendado: ele entra e grava esta reunião.',
        falha: 'Não foi possível agendar o bot',
      });
      return;
    }

    this.confirmationService
      .confirm(
        'Desligar o bot desta reunião?',
        `O bot não entra em "${meeting.title}" e ela não é gravada. Dá para ligar de novo até a reunião começar.`,
        'danger',
        undefined,
        'Desligar'
      )
      .pipe(
        first(),
        filter(confirmado => confirmado)
      )
      .subscribe(() =>
        this.executar(meeting, this.meetingsService.cancelBot(meeting.id), {
          sucesso: 'Bot desligado para esta reunião.',
          falha: 'Não foi possível desligar o bot',
        })
      );
  }

  /** Roda a ação com a linha travada, avisa o resultado e atualiza a linha e os números do topo. */
  private executar(
    meeting: MeetingDto,
    acao: Observable<MeetingDto | null>,
    mensagens: { sucesso: string; falha: string }
  ): void {
    this.ocupada.set(meeting.id);
    acao
      .pipe(
        first(),
        finalize(() => this.ocupada.set(null)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: atualizada => {
          this.toast.showToastSuccess(mensagens.sucesso);
          this.atualizarLinha(atualizada);
        },
        error: erro =>
          this.toast.showToastError(
            mensagens.falha,
            mensagemDoErro(erro, 'Tente de novo em alguns instantes.')
          ),
      });
  }

  /**
   * Arquivar, restaurar ou excluir (card 3786), sempre com confirmação. A reunião muda de aba (ou some),
   * então a página e os números do topo recarregam em vez de trocar a linha.
   */
  protected executeLifecycleAction({ meeting, action }: MeetingLifecycleRequest): void {
    const texts = meetingLifecycleTexts(action, meeting);
    this.confirmationService
      .confirm(texts.title, texts.description, texts.variant, undefined, texts.acceptLabel)
      .pipe(
        first(),
        filter(confirmed => confirmed)
      )
      .subscribe(() => {
        const request: Observable<unknown> =
          action === 'archive'
            ? this.meetingsService.archive(meeting.id)
            : action === 'restore'
              ? this.meetingsService.restore(meeting.id)
              : this.meetingsService.delete(meeting.id);

        this.ocupada.set(meeting.id);
        request
          .pipe(
            first(),
            finalize(() => this.ocupada.set(null)),
            takeUntilDestroyed(this.destroyRef)
          )
          .subscribe({
            next: () => {
              this.toast.showToastSuccess(texts.successMessage);
              this.carregar();
              this.carregarIndicadores();
            },
            error: error =>
              this.toast.showToastError(
                texts.failureTitle,
                mensagemDoErro(error, 'Tente de novo em alguns instantes.')
              ),
          });
      });
  }

  /**
   * Bot enviado por "Adicionar bot na call" (card 3134): a reunião nova entra na lista e nos números.
   */
  protected aoEnviarBot(): void {
    this.carregar();
    this.carregarIndicadores();
  }

  /**
   * Troca a linha pela versão devolvida pelo backend. Com filtro que a linha pode ter deixado de
   * satisfazer ("sem vínculo", "com bot") ou reunião que voltou a privada, recarrega a página em vez
   * de mostrar o que o recorte exclui.
   */
  protected atualizarLinha(atualizada: MeetingDto | null): void {
    const recorteMuda =
      !!this.filtros().onlyUnlinked || (this.aba() === 'proximas' && this.soComBot());
    this.carregarIndicadores();
    if (!atualizada || recorteMuda) {
      this.carregar();
      return;
    }
    this.ligacoes.update(lista => lista.map(l => (l.id === atualizada.id ? atualizada : l)));
  }
}
